import cx from 'classnames'
import React, { Component } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSignOut } from '@fortawesome/pro-regular-svg-icons/faSignOut'
import { faLongArrowLeft } from '@fortawesome/pro-regular-svg-icons/faLongArrowLeft'
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown'
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons/faChevronRight'
import { faUser } from '@fortawesome/pro-solid-svg-icons/faUser'
import { faFileExport } from '@fortawesome/pro-solid-svg-icons/faFileExport'
import { faUserFriends } from '@fortawesome/pro-solid-svg-icons/faUserFriends'
import { faBuilding } from '@fortawesome/pro-solid-svg-icons/faBuilding'
import { faBells } from '@fortawesome/pro-solid-svg-icons/faBells'

import Button from '@softwaredev_cegeka/stix-core/dist/components/Button'
import Dropdown from '@softwaredev_cegeka/stix-core/dist/components/Dropdown'
import AuthApi from '@softwaredev_cegeka/auth-api'

import { reverse } from 'src/routes/config'

import MenuItem from 'src/components/Menu/MenuItem'
import withUserData from 'src/HOCs/withUserData'
import withSubsidiaryData from 'src/HOCs/withSubsidiaryData'
import OptionSelector from 'src/components/ImpersonationOptionSelector'
import SlideAnimation from 'src/components/SlideAnimation'
import { UserDataContext } from 'src/HOCs/withAuth'
import {
    isAnyAllowed,
    isCegekaEmployee,
    getCompanyID,
    HR_PROFILE_PERMISSION,
} from 'src/utils/permissions'
import { isGlobalAdmin } from 'src/utils/permissions'
import withTranslations from 'src/HOCs/withTranslations'
import getNameInitials from 'src/utils/getNameInitials'
import isImpersonatingUser from 'src/utils/isImpersonatingUser'
import isImpersonatingOrganizationOnly from 'src/utils/isImpersonatingOrganizationOnly'

// Style
import menuStyles from '../menu.module.scss'
import styles from './userMenu.module.scss'

const MENU_ITEMS_IDS = {
    profileSettings: 'profile-settings',
    notificationsSettings: 'notifications-settings',
    teamProfiles: 'my-team-profile',
    myExports: 'my-exports',
    impersonateUser: 'impersonate-user',
    changeOrganization: 'changeOrganization',
    myOrders: 'my-orders',
    logOut: 'logout',
}

const HAS_LEVEL_TWO = [
    MENU_ITEMS_IDS.impersonateUser,
    MENU_ITEMS_IDS.changeOrganization,
]

const MENU_ITEMS = [
    {
        id: MENU_ITEMS_IDS.profileSettings,
        startIcon: faUser,
        text: 'Account',
        to: reverse('profile-settings'),
    },
    {
        id: MENU_ITEMS_IDS.notificationsSettings,
        startIcon: faBells,
        text: 'Notifications',
        to: '/notifications/settings',
    },
    {
        id: MENU_ITEMS_IDS.myExports,
        startIcon: faFileExport,
        text: 'My exports',
        to: '/my-exports',
    },
    {
        id: MENU_ITEMS_IDS.impersonateUser,
        startIcon: faUserFriends,
        text: 'Impersonate user',
        endIcon: faChevronRight,
        permissions: userData =>
            (isGlobalAdmin(userData) || userData.impersonatedUser) &&
            userData.shouldSeeImpersonator,
    },
    {
        id: MENU_ITEMS_IDS.changeOrganization,
        startIcon: faBuilding,
        text: 'Change organization',
        endIcon: faChevronRight,
        permissions: (userData, state) => state.organizations.length > 1,
    },
    {
        id: MENU_ITEMS_IDS.logOut,
        startIcon: faSignOut,
        text: 'Log out',
        to: reverse('logout'),
        endIcon: faChevronRight,
    },
]

const AvatarButton = React.forwardRef((props, ref) => {
    const { defaultOrg, userData, ...rest } = props
    const { impersonatedOrganization, data } = userData
    const isOpen = props['aria-expanded']

    delete rest.bsPrefix
    delete rest.className
    delete rest.impersonatedOrg
    delete rest.startIcon
    delete rest.endIcon

    return (
        <button
            {...rest}
            className={cx(styles.userMenuBtn, {
                [styles.isMenuOpen]: isOpen,
            })}
        >
            <span className={styles.initials}>
                {getNameInitials(data.firstName, data.lastName)}
            </span>
            <div className={styles.userDataWrapper}>
                {/* hzn-dt-user-tag is used for tagging user sessions in Dynatrace */}
                <span
                    className={cx(styles.userFirstLastName, 'hzn-dt-user-tag')}
                >
                    {data?.firstName} {data?.lastName}
                    <FontAwesomeIcon
                        icon={faChevronDown}
                        title={isOpen ? 'Close user menu' : 'Open user menu'}
                        className={styles.userMenuIcon}
                    />
                </span>
                <span className={styles.impersonatedTenant}>
                    {impersonatedOrganization?.label || defaultOrg?.name}
                </span>
            </div>
        </button>
    )
})

class UserMenu extends Component {
    static contextType = UserDataContext
    usersCancelId = Symbol('FetchUsers')
    authApi = new AuthApi(this.context.authToken || '')

    state = {
        defaultOrg: null,
        // used to not trigger animation when the menu is open for the first time
        initialOpen: true,
        isOpen: false,
        organizations: [],
        organizationSearch: '',
        secondLevelItemId: null,
        users: [],
    }

    checkPermissions(menuItem) {
        const { userData } = this.props
        if (menuItem.id === 'profile-settings') {
            return isAnyAllowed(this.props.userData, [HR_PROFILE_PERMISSION])
        }
        if (
            !menuItem?.permissions ||
            (userData.isImpersonating &&
                menuItem.id === 'impersonate-user' &&
                isImpersonatingUser(userData))
        ) {
            return true
        }

        return menuItem.permissions(this.props.userData, this.state)
    }

    componentDidMount() {
        this.fetchOrganizations()
    }

    // Only update when defaultOrg actually changes
    componentDidUpdate(_, prevState) {
        const { defaultOrg } = this.state
        const prevDefaultOrg = prevState.defaultOrg

        // Only update if defaultOrg changed AND it's not null
        if (defaultOrg && defaultOrg !== prevDefaultOrg) {
            this.updateOrgLabelInContext()
        }
    }

    // Check if values actually need updating before calling setters
    updateOrgLabelInContext = () => {
        const { defaultOrg } = this.state
        const { userData } = this.props

        if (!defaultOrg || !userData) {
            return
        }

        const { impersonatedOrganization } = this.context

        if (
            userData.setDefaultOrg &&
            defaultOrg &&
            userData.defaultOrg !== defaultOrg
        ) {
            userData.setDefaultOrg(defaultOrg)
        }

        if (
            userData.setImpersonatedOrganization &&
            impersonatedOrganization &&
            userData.impersonatedOrganization !== impersonatedOrganization
        ) {
            userData.setImpersonatedOrganization(impersonatedOrganization)
        }
    }

    /**
     * Fetch organizations which can be impersonated
     */
    fetchOrganizations = async searchValue => {
        const { data } = this.context
        try {
            const response = await this.authApi.fetchTenants({
                username: data.username,
                name_like: searchValue,
                //FIXME: it looks like pagination and sort does not work in the backend
                per_page: 20,
                sort: '+name',
            })
            !searchValue &&
                localStorage.setItem(
                    'organizations',
                    JSON.stringify(response.data.tenants)
                )

            const organizations = response?.data.tenants
            const defaultOrg = organizations.find(
                organization =>
                    organization.organization_id === data.organization_id
            )

            this.setState({
                defaultOrg,
                organizations: organizations,
            })
        } catch (error) {
            console.error(error)
        }
    }

    getUserLabelWithOrganization = user => {
        const fullName = `${user.first_name} ${user.last_name}`
        if (user.tenants.length === 0) return fullName
        if (
            isCegekaEmployee({
                data: {
                    organization_id: user.tenants[0]?.tenant,
                },
            })
        )
            return `${fullName} ( Cegeka )`
        return `${fullName} ( ${user.tenants[0]?.tenant_name} )`
    }

    /**
     * Fetch users which can be impersonated
     */
    fetchUsers = async searchValue => {
        const [part1, ...parts] = searchValue.trim().split(' ')
        const company_id = getCompanyID(this.context)
        try {
            const res = await this.authApi.fetchUsers(
                {
                    first_name_like: part1,
                    last_name_like: parts.length > 0 ? parts.join(' ') : part1,
                    email_like: part1,
                    use_or: true,
                    per_page: 20,
                    sort: '+first_name',
                    load_relationships: true,
                    company_id: company_id,
                },
                this.usersCancelId
            )

            this.setState({
                users: res.data.results.map(user => ({
                    ...user,
                    label: this.getUserLabelWithOrganization(user),
                    id: user.id.toString(),
                })),
            })
        } catch (error) {
            console.error(error)
            this.setState({ users: [] })
        }
    }

    /**
     * Get the organization of the current impersonated user. Used for displaying it
     * under the name in the user menu
     *
     * @param organizationId
     * @returns {null | {label, value}}
     */
    getDefaultOrganization(organizationId) {
        const { organizations } = this.state
        const defaultOrg = organizations.find(
            org => org.organization_id === organizationId
        )

        if (!defaultOrg) {
            return null
        }
        return { label: defaultOrg.name, value: organizationId }
    }

    /**
     * Used for loading organization and user permissions, taking the current organization_id into account.
     *
     * @param username
     * @param authToken
     * @param params
     * @returns {Promise<*|*[]>}
     */
    async loadPermissions(username, authToken, params = {}) {
        try {
            if (authToken) {
                // In some cases we need to update the token again. For instance after stopping an impersonation,
                // we want the currently active user to fetch its permissions.
                this.authApi.updateToken(authToken)
            }
            const permissionResponse = await this.authApi.fetchAppPermissions({
                username: username || this.context.data.username,
                ...params,
            })
            const { app_permissions } = permissionResponse.data
            if (!app_permissions || app_permissions.length === 0) {
                return []
            }
            // Returns an array of objects, but we should only need the permissions of the current app.
            return app_permissions[0].actions || []
        } catch (e) {
            console.error(e)
        }
    }

    /**
     * @param [user]
     * @returns {Promise<void>}
     */
    impersonateUser = async user => {
        const { userData } = this.props
        if (!user) {
            return userData.stopImpersonateUser()
        }
        if (userData.data.orig_organization_id) {
            return userData.impersonateUser(user.username)
        }
        if (isImpersonatingUser(userData)) {
            return userData.impersonate(
                user.username,
                userData.data.organization_id
            )
        }
        return userData.impersonateUser(user.username)
    }

    getFilteredOrganizationOptions() {
        const { organizations, organizationSearch } = this.state
        const searchTerm = organizationSearch.toLowerCase()
        return organizations
            .filter(org => org.name.toLowerCase().includes(searchTerm))
            .map(org => ({
                ...org,
                // The [OptionSelector] needs a label and an id to work. In this case we will overwrite
                // the id of [org] (which is the id of the item from the database) with the organization_id
                // so we will be able to see which org is selected
                id: org.organization_id,
                label: org.name,
            }))
    }

    /**
     * Impersonate an organization. When impersonating an organization an api call
     * is done fetching that organizations permissions.
     *
     * @param organization
     * @returns {Promise<void>}
     */
    impersonateOrganization = organization => {
        const { userData } = this.props

        // User currently not impersonating and selecting it's own organization
        if (
            !userData.isImpersonating &&
            organization?.id === userData?.data?.organization_id
        ) {
            return
        }

        if (
            !organization ||
            // User currently impersonating organization only, and switches back to it's organization
            (isImpersonatingOrganizationOnly(userData) &&
                userData.data.orig_organization_id === organization.id)
        ) {
            return this.props.userData.stopImpersonateOrganization()
        }
        if (isImpersonatingUser(userData)) {
            return userData.impersonate(
                userData.data.username,
                organization.organization_id
            )
        }
        return this.props.userData.impersonateOrganization(
            organization.organization_id
        )
    }

    /**
     * Handle list item click
     */
    handleListItemClick = id => {
        const { initialOpen } = this.state

        if (initialOpen) {
            this.setState({ initialOpen: false })
        }

        if (HAS_LEVEL_TWO.includes(id)) {
            this.setState({ secondLevelItemId: id })
        } else {
            this.setState({ isOpen: false })
        }
    }

    /**
     * Handle go back to level 1 of the menu
     */
    handleGoBack = () => {
        this.setState({ secondLevelItemId: null })
    }

    /**
     * Handle open user menu
     *
     * @param {boolean} _
     * @param {React.MouseEvent<HTMLElement>} event
     */
    handleToggleUserMenu = (_, event) => {
        const { isOpen } = this.state

        if (!isOpen) {
            this.setState({ isOpen: true })
        }

        // When [event] is false it means that the user clicked outside the dropdown
        // so the menu should be closed
        if (!event) {
            this.setState({
                initialOpen: true,
                isOpen: false,
                secondLevelItemId: null,
                users: [],
            })
        }
    }

    /**
     * Builds the URL parameters for the terms of use link.
     *
     * @returns {string} - The URL parameters string.
     */
    _getTermsOfUseUrlParams() {
        const { i18n } = this.props
        const urlParams = new URLSearchParams({ lang: i18n.language })
        return urlParams.toString()
    }

    /**
     * Returns second level of the menu when a menu item is pressed
     *
     * @returns JSX.Element
     */
    _renderFirstLevel() {
        const { initialOpen, secondLevelItemId } = this.state
        const { t } = this.props

        return (
            <SlideAnimation
                direction={initialOpen ? undefined : 'left'}
                type={secondLevelItemId ? 'out' : 'in'}
                visible={secondLevelItemId === null}
            >
                {MENU_ITEMS.map(
                    menuItem =>
                        this.checkPermissions(menuItem) && (
                            <MenuItem
                                key={menuItem.id}
                                onClick={this.handleListItemClick}
                                {...menuItem}
                            >
                                {t(menuItem.text)}
                            </MenuItem>
                        )
                )}
                <div className={styles.footer}>
                    <a
                        href={`/_terms-of-use?${this._getTermsOfUseUrlParams()}`}
                        className={styles.detailsLink}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {t('Terms & conditions')}
                    </a>
                    {/*we want to keep the referrer for analytics, this is a cegeka domain anyway.*/}
                    {/* eslint-disable-next-line */}
                    <a
                        target="_blank"
                        rel="noopener noreferrer"
                        href={`/_cookie-policy?${this._getTermsOfUseUrlParams()}`}
                        className={styles.detailsLink}
                    >
                        {t('Cookie policy')}
                    </a>
                </div>
            </SlideAnimation>
        )
    }

    /**
     * Returns first level of menu items
     *
     * @returns JSX.Element
     */
    _renderSecondLevel() {
        const { secondLevelItemId, users, defaultOrg } = this.state
        const { t, userData } = this.props
        const { isImpersonating } = this.context
        const { data } = userData
        const { organization_id, firstName, lastName } = data
        const selectedMenuItem = MENU_ITEMS.find(
            menuItem => menuItem.id === secondLevelItemId
        )
        const renderImpersonationLabel = () => {
            const orgName = isCegekaEmployee(userData)
                ? 'Cegeka'
                : defaultOrg.name
            return {
                ...data,
                label: `${firstName} ${lastName} ( ${orgName} )`,
            }
        }
        let secondLevelContent
        if (selectedMenuItem?.id === MENU_ITEMS_IDS.impersonateUser) {
            secondLevelContent = (
                <OptionSelector
                    options={users}
                    onSearch={this.fetchUsers}
                    onApplySelection={this.impersonateUser}
                    onRemoveSelection={async () => {
                        await this.impersonateUser(null)
                    }}
                    searchLabel={t('Search a user')}
                    searchPlaceholder={t('Search a user')}
                    searchKey={'username'}
                    selectedOption={
                        isImpersonatingUser(userData) &&
                        renderImpersonationLabel()
                    }
                    shouldFocusInput={true}
                />
            )
        } else if (selectedMenuItem?.id === MENU_ITEMS_IDS.changeOrganization) {
            secondLevelContent = (
                <OptionSelector
                    options={this.getFilteredOrganizationOptions()}
                    onSearch={searchValue => {
                        this.setState({
                            organizationSearch: searchValue,
                        })
                    }}
                    onApplySelection={this.impersonateOrganization}
                    onRemoveSelection={async () => {
                        await this.impersonateOrganization(null)
                    }}
                    searchLabel={t('Search an organization')}
                    searchPlaceholder={t('Search an organization')}
                    selectedOption={
                        isImpersonating && {
                            id: organization_id,
                            label: defaultOrg.name,
                        }
                    }
                    shouldFocusInput={true}
                />
            )
        }

        return (
            <SlideAnimation
                direction="right"
                type={selectedMenuItem ? 'in' : 'out'}
                visible={secondLevelItemId !== null}
            >
                <div className={styles.secondLevelContainerHeader}>
                    <Button
                        className={styles.backButton}
                        variant="naked"
                        onClick={this.handleGoBack}
                    >
                        <FontAwesomeIcon icon={faLongArrowLeft} />
                    </Button>

                    <h2 className={styles.secondLevelTitle}>
                        {selectedMenuItem ? t(selectedMenuItem?.text) : ''}
                    </h2>
                </div>

                {secondLevelContent}
            </SlideAnimation>
        )
    }

    render() {
        const { defaultOrg, isOpen, secondLevelItemId } = this.state
        const { userData } = this.props

        return (
            <Dropdown
                className={cx(menuStyles.dropdown, styles.dropdown, {
                    [styles.dropdownSecondLevel]:
                        secondLevelItemId !== null &&
                        secondLevelItemId !== MENU_ITEMS_IDS.profileSettings,
                })}
                alignRight
                show={isOpen}
                onToggle={this.handleToggleUserMenu}
            >
                <Dropdown.Toggle
                    as={AvatarButton}
                    id="user-dropdown-menu"
                    impersonatedOrg={this.context.impersonatedOrganization}
                    defaultOrg={defaultOrg}
                    userData={userData}
                />
                <Dropdown.Menu>
                    {this._renderFirstLevel()}
                    {this._renderSecondLevel()}
                </Dropdown.Menu>
            </Dropdown>
        )
    }
}

export default withTranslations(
    'common',
    'UserMenu'
)(withSubsidiaryData(withUserData(UserMenu)))
